/**
 * `ColumnDef` lists for the Cloud Zone detail tables: hosts (cpu/mem usage
 * bars), datastores (capacity + usage bar) and instances (vcpu/mem link).
 * Read-only. Reuses the shared cloud status mapper + usage bar cell.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { StatusPill } from '@vnetwork/ui/data';
import type { CloudHost, CloudDatastore, CloudZoneInstance, CloudZone } from '@/api/capacities-cloud';
import { TableLink } from '@/components/table-link';
import { formatGb, pct1 } from '../dashboard/capacity-format';
import { cloudStatusView, mbToGb } from './cloud-format';
import { UsagePctText, NumCell, numHeader } from './usage-pct-cell';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const dash = <span style={muted}>—</span>;

export const hostColumns: ColumnDef<CloudHost, unknown>[] = [
  {
    id: 'name',
    header: 'Host Name',
    cell: ({ row }) => (
      <span title={row.original.name} style={{ overflowWrap: 'anywhere' }}>
        {row.original.name || row.original.id}
      </span>
    ),
  },
  {
    id: 'cpu_cores',
    header: numHeader('CPU Cores'),
    cell: ({ row }) => <NumCell>{row.original.cpu_cores ?? 0}</NumCell>,
  },
  {
    id: 'memory',
    header: numHeader('Memory'),
    cell: ({ row }) => <NumCell>{formatGb(mbToGb(row.original.memory_mb))}</NumCell>,
  },
  {
    id: 'vm_count',
    header: numHeader('VMs'),
    cell: ({ row }) => <NumCell>{row.original.vm_count ?? 0}</NumCell>,
  },
  {
    id: 'cpu_usage',
    header: numHeader('CPU Usage'),
    cell: ({ row }) => <UsagePctText value={row.original.cpu_usage_pct} />,
  },
  {
    id: 'memory_usage',
    header: numHeader('Memory Usage'),
    cell: ({ row }) => <UsagePctText value={row.original.memory_usage_pct} />,
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const v = cloudStatusView(row.original.status);
      return <StatusPill tone={v.tone}>{v.label}</StatusPill>;
    },
  },
];

export const datastoreColumns: ColumnDef<CloudDatastore, unknown>[] = [
  {
    id: 'name',
    header: 'Datastore Name',
    cell: ({ row }) => (
      <span title={row.original.name} style={{ overflowWrap: 'anywhere' }}>
        {row.original.name || row.original.id}
      </span>
    ),
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => (row.original.type ? <span style={{ textTransform: 'uppercase' }}>{row.original.type}</span> : dash),
  },
  {
    id: 'capacity',
    header: numHeader('Capacity'),
    cell: ({ row }) => <NumCell>{formatGb(row.original.capacity_gb)}</NumCell>,
  },
  {
    id: 'used',
    header: numHeader('Used'),
    cell: ({ row }) => <NumCell>{formatGb(row.original.used_gb)}</NumCell>,
  },
  {
    id: 'free',
    header: numHeader('Free'),
    cell: ({ row }) => <NumCell>{formatGb(row.original.free_gb)}</NumCell>,
  },
  {
    id: 'usage',
    header: numHeader('Usage'),
    cell: ({ row }) => {
      const cap = Number(row.original.capacity_gb) || 0;
      const used = Number(row.original.used_gb) || 0;
      return <UsagePctText value={cap > 0 ? (used / cap) * 100 : 0} />;
    },
  },
  {
    id: 'accessible',
    header: 'Accessible',
    cell: ({ row }) => (
      <StatusPill tone={row.original.accessible ? 'success' : 'danger'}>
        {row.original.accessible ? 'Yes' : 'No'}
      </StatusPill>
    ),
  },
];

export const instanceColumns: ColumnDef<CloudZoneInstance, unknown>[] = [
  {
    id: 'name',
    header: 'Instance',
    cell: ({ row }) => {
      const label = row.original.name || row.original.uid;
      return (
        <TableLink to={`/capacities/cloud/instances/${row.original.uid}`} title={label}>
          {label}
        </TableLink>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const v = cloudStatusView(row.original.status);
      return <StatusPill tone={v.tone}>{v.label}</StatusPill>;
    },
  },
  {
    id: 'vcpus',
    header: numHeader('vCPU'),
    cell: ({ row }) => <NumCell>{row.original.spec?.cpu ?? 0}</NumCell>,
  },
  {
    id: 'memory',
    header: numHeader('Memory'),
    cell: ({ row }) => <NumCell>{formatGb(mbToGb(row.original.spec?.memory_mb))}</NumCell>,
  },
  {
    id: 'disk',
    header: numHeader('Disk'),
    cell: ({ row }) => <NumCell>{formatGb(row.original.spec?.disk_gb)}</NumCell>,
  },
  {
    id: 'host',
    header: 'Host',
    cell: ({ row }) =>
      row.original.host_name ? (
        <span title={row.original.host_name} style={{ overflowWrap: 'anywhere' }}>
          {row.original.host_name}
        </span>
      ) : (
        dash
      ),
  },
];

/** Compute-zone list columns: name links to the zone drill-down + status pill. */
export const zoneListColumns: ColumnDef<CloudZone, unknown>[] = [
  {
    id: 'zone',
    header: 'Zone',
    cell: ({ row }) => (
      <TableLink to={`/capacities/cloud/zones/${row.original.zone_id}`} title={row.original.zone_name}>
        {row.original.zone_name || row.original.zone_id}
      </TableLink>
    ),
  },
  {
    id: 'provider',
    header: 'Provider',
    cell: ({ row }) =>
      row.original.provider_name ? <span>{row.original.provider_name}</span> : dash,
  },
  {
    id: 'hosts',
    header: numHeader('Hosts'),
    cell: ({ row }) => <NumCell>{row.original.host_count ?? 0}</NumCell>,
  },
  {
    id: 'vms',
    header: numHeader('VMs'),
    cell: ({ row }) => <NumCell>{row.original.vm_count ?? 0}</NumCell>,
  },
  {
    id: 'cpu',
    header: numHeader('CPU'),
    cell: ({ row }) => <UsagePctText value={row.original.cpu_usage_pct} />,
  },
  {
    id: 'memory',
    header: numHeader('Memory'),
    cell: ({ row }) => <UsagePctText value={row.original.memory_usage_pct} />,
  },
  {
    id: 'storage',
    header: numHeader('Storage'),
    cell: ({ row }) =>
      row.original.storage_gb != null ? <NumCell>{formatGb(row.original.storage_gb)}</NumCell> : dash,
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const v = cloudStatusView(row.original.status);
      return <StatusPill tone={v.tone}>{v.label}</StatusPill>;
    },
  },
];

/** Shared: percentage label helper for zone summary cards. */
export const asPct = (v: number | null | undefined) => `${pct1(v)}%`;
